import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import CalculateIcon from "@mui/icons-material/Calculate";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import DataGridViewport from "../components/DataGridViewport";
import LocalSelector from "./components/LocalSelector";
import { useAppConfig } from "../hooks/AppConfigContext";
import { useProveedores } from "../hooks/ProveedoresContext";
import { useUserContext } from "../hooks/UserContext";
import {
  fetchAplicarModificacionPrecios,
  fetchGetCatalogosModificacionPrecios,
  fetchGetProductosModificacionPrecios,
  fetchPreviewModificacionPrecios,
  ProductoCatalogoItem,
  ProductoPrecioMasivoItem,
  ProductoPrecioMasivoRequest,
} from "../services/productoService";
import { isAbortError } from "../services/fetchService";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import { useNotification } from "../hooks/NotificationContext";

const toMoney = (value: number | undefined) => CurrencyFormatter.format(Number(value ?? 0));
const toPercent = (value: number | undefined) => `${Number(value ?? 0).toFixed(2)} %`;

const ModificacionMasivaPrecios = () => {
  const { user } = useUserContext();
  const { showNotification } = useNotification();
  const { productoVentaHeaders } = useAppConfig();
  const { proveedores } = useProveedores();
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [rubros, setRubros] = useState<ProductoCatalogoItem[]>([]);
  const [subrubros, setSubrubros] = useState<ProductoCatalogoItem[]>([]);
  const [idRubro, setIdRubro] = useState<number | undefined>(undefined);
  const [idSubrubro, setIdSubrubro] = useState<number | undefined>(undefined);
  const [idProveedor, setIdProveedor] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [aplicarATodoLoFiltrado, setAplicarATodoLoFiltrado] = useState(false);
  const [productos, setProductos] = useState<ProductoPrecioMasivoItem[]>([]);
  const [preview, setPreview] = useState<ProductoPrecioMasivoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [tipoModificacion, setTipoModificacion] = useState<ProductoPrecioMasivoRequest["tipoModificacion"]>("%");
  const [aumentar, setAumentar] = useState(true);
  const [valor, setValor] = useState("");
  const [modificarCompra, setModificarCompra] = useState(false);
  const [modificarContado, setModificarContado] = useState(true);
  const [modificarMayor, setModificarMayor] = useState(false);
  const [modificarMenor, setModificarMenor] = useState(false);
  const [modificarMargenContado, setModificarMargenContado] = useState(false);
  const [modificarMargenMayor, setModificarMargenMayor] = useState(false);
  const [modificarMargenMenor, setModificarMargenMenor] = useState(false);
  const [noModificarPrecioVenta, setNoModificarPrecioVenta] = useState(false);
  const [redondearSinDecimales, setRedondearSinDecimales] = useState(false);
  const [ajustarDecimal99, setAjustarDecimal99] = useState(false);

  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();
    fetchGetCatalogosModificacionPrecios(user, idRubro, controller.signal)
      .then((catalogos) => {
        setRubros(catalogos.rubros);
        setSubrubros(catalogos.subrubros);
      })
      .catch((error) => {
        if (!isAbortError(error)) showNotification("No se pudieron cargar los filtros.", "error");
      });

    return () => controller.abort();
  }, [user, idRubro, showNotification]);

  useEffect(() => {
    if (!user || !idLocal) {
      setProductos([]);
      setTotal(0);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    fetchGetProductosModificacionPrecios(idLocal, user, paginationModel.page, paginationModel.pageSize, {
      search,
      idRubro,
      idSubrubro,
      idProveedor,
    }, controller.signal)
      .then((data) => {
        setProductos(data.items);
        setTotal(data.total);
      })
      .catch((error) => {
        if (!isAbortError(error)) showNotification("No se pudieron cargar los productos.", "error");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [user, idLocal, paginationModel.page, paginationModel.pageSize, search, idRubro, idSubrubro, idProveedor, refreshKey, showNotification]);

  const hasMarginMode = modificarMargenContado || modificarMargenMayor || modificarMargenMenor;
  const contadoHeader = productoVentaHeaders.CONTADO?.Header ?? "Contado";
  const mayorHeader = productoVentaHeaders.MAYOR?.Header ?? "Mayor";
  const menorHeader = productoVentaHeaders.MENOR?.Header ?? "Menor";

  useEffect(() => {
    if (hasMarginMode) {
      setModificarContado(false);
      setModificarMayor(false);
      setModificarMenor(false);
    }
  }, [hasMarginMode]);

  const buildRequest = (): ProductoPrecioMasivoRequest | undefined => {
    const numericValue = Number(valor.replace(",", "."));
    if (!idLocal || !Number.isFinite(numericValue) || numericValue <= 0) {
      showNotification("Seleccione local e ingrese un valor valido.", "warning");
      return undefined;
    }

    if (!aplicarATodoLoFiltrado && selectedIds.length === 0) {
      showNotification("Seleccione productos o active aplicar a todo lo filtrado.", "warning");
      return undefined;
    }

    if (!modificarCompra && !modificarContado && !modificarMayor && !modificarMenor && !modificarMargenContado && !modificarMargenMayor && !modificarMargenMenor) {
      showNotification("Seleccione al menos un campo para modificar.", "warning");
      return undefined;
    }

    return {
      idLocal: Number(idLocal),
      idProductos: selectedIds,
      aplicarATodoLoFiltrado,
      search,
      idRubro,
      idSubrubro,
      idProveedor,
      tipoModificacion,
      aumentar,
      valor: numericValue,
      modificarCompra,
      modificarContado,
      modificarMayor,
      modificarMenor,
      modificarMargenContado,
      modificarMargenMayor,
      modificarMargenMenor,
      noModificarPrecioVenta,
      redondearSinDecimales,
      ajustarDecimal99,
    };
  };

  const handlePreview = () => {
    const request = buildRequest();
    if (!user || !request) return;

    setPreviewing(true);
    fetchPreviewModificacionPrecios(user, request)
      .then((data) => {
        setPreview(data.items);
        showNotification(`Vista previa generada para ${data.total} productos.`, "success");
      })
      .catch((err) => showNotification(err?.message ?? "No se pudo generar la vista previa.", "error"))
      .finally(() => setPreviewing(false));
  };

  const handleApply = () => {
    const request = buildRequest();
    if (!user || !request) return;

    setSaving(true);
    fetchAplicarModificacionPrecios(user, request)
      .then((data) => {
        showNotification(data.message ?? "Operacion completada.", "success");
        setConfirmOpen(false);
        setPreview([]);
        setSelectedIds([]);
        setRefreshKey((current) => current + 1);
      })
      .catch((err) => showNotification(err?.message ?? "No se pudo aplicar la modificacion.", "error"))
      .finally(() => setSaving(false));
  };

  const productColumns = useMemo<GridColDef[]>(() => [
    { field: "codigo", headerName: "Codigo", minWidth: 110 },
    { field: "producto", headerName: "Producto", minWidth: 240, flex: 1 },
    { field: "rubro", headerName: "Rubro", minWidth: 140 },
    { field: "subrubro", headerName: "Subrubro", minWidth: 140 },
    { field: "proveedor", headerName: "Proveedor", minWidth: 170 },
    { field: "costo", headerName: "Compra", minWidth: 120, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "contado", headerName: contadoHeader, minWidth: 120, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "mayor", headerName: mayorHeader, minWidth: 120, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "menor", headerName: menorHeader, minWidth: 120, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
  ], [contadoHeader, mayorHeader, menorHeader]);

  const previewColumns = useMemo<GridColDef[]>(() => [
    { field: "codigo", headerName: "Codigo", minWidth: 110 },
    { field: "producto", headerName: "Producto", minWidth: 240, flex: 1 },
    { field: "costoNuevo", headerName: "Compra nueva", minWidth: 130, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "contadoNuevo", headerName: `${contadoHeader} nuevo`, minWidth: 130, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "mayorNuevo", headerName: `${mayorHeader} nuevo`, minWidth: 130, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "menorNuevo", headerName: `${menorHeader} nuevo`, minWidth: 130, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "margenContadoNuevo", headerName: `Margen ${contadoHeader}`, minWidth: 140, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toPercent(value) },
    { field: "margenMayorNuevo", headerName: `Margen ${mayorHeader}`, minWidth: 130, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toPercent(value) },
    { field: "margenMenorNuevo", headerName: `Margen ${menorHeader}`, minWidth: 130, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toPercent(value) },
  ], [contadoHeader, mayorHeader, menorHeader]);

  const productRows = productos.map((item) => ({ ...item, id: item.idProducto }));
  const previewRows = preview.map((item) => ({ ...item, id: item.idProducto }));

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h4" gutterBottom>Modificacion masiva de precios</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}><LocalSelector onLocalChange={setIdLocal} /></Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Buscar codigo/producto" value={search} onChange={(e) => { setSearch(e.target.value); setPaginationModel((p) => ({ ...p, page: 0 })); }} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Rubro</InputLabel>
              <Select value={idRubro ?? ""} label="Rubro" onChange={(e) => { setIdRubro(e.target.value ? Number(e.target.value) : undefined); setIdSubrubro(undefined); setPaginationModel((p) => ({ ...p, page: 0 })); }}>
                <MenuItem value="">Todos</MenuItem>
                {rubros.map((item) => <MenuItem key={item.id} value={item.id}>{item.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Subrubro</InputLabel>
              <Select value={idSubrubro ?? ""} label="Subrubro" onChange={(e) => { setIdSubrubro(e.target.value ? Number(e.target.value) : undefined); setPaginationModel((p) => ({ ...p, page: 0 })); }}>
                <MenuItem value="">Todos</MenuItem>
                {subrubros.map((item) => <MenuItem key={item.id} value={item.id}>{item.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Proveedor</InputLabel>
              <Select value={idProveedor ?? ""} label="Proveedor" onChange={(e) => { setIdProveedor(e.target.value ? Number(e.target.value) : undefined); setPaginationModel((p) => ({ ...p, page: 0 })); }}>
                <MenuItem value="">Todos</MenuItem>
                {proveedores.map((item) => <MenuItem key={item.id} value={item.id}>{item.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={2}>
            <TextField label="Valor" value={valor} onChange={(e) => setValor(e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select value={tipoModificacion} label="Tipo" onChange={(e) => setTipoModificacion(e.target.value as ProductoPrecioMasivoRequest["tipoModificacion"])}>
                <MenuItem value="%">Porcentaje</MenuItem>
                <MenuItem value="$$">Valor fijo</MenuItem>
                <MenuItem value="Z">Aumento fijo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Operacion</InputLabel>
              <Select value={aumentar ? "1" : "0"} label="Operacion" disabled={tipoModificacion === "$$"} onChange={(e) => setAumentar(e.target.value === "1")}>
                <MenuItem value="1">Aumentar</MenuItem>
                <MenuItem value="0">Bajar</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <FormControlLabel control={<Checkbox checked={modificarCompra} onChange={(e) => setModificarCompra(e.target.checked)} />} label="Compra" />
              <FormControlLabel control={<Checkbox checked={modificarContado} disabled={hasMarginMode} onChange={(e) => setModificarContado(e.target.checked)} />} label={contadoHeader} />
              <FormControlLabel control={<Checkbox checked={modificarMayor} disabled={hasMarginMode} onChange={(e) => setModificarMayor(e.target.checked)} />} label={mayorHeader} />
              <FormControlLabel control={<Checkbox checked={modificarMenor} disabled={hasMarginMode} onChange={(e) => setModificarMenor(e.target.checked)} />} label={menorHeader} />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <FormControlLabel control={<Checkbox checked={modificarMargenContado} onChange={(e) => setModificarMargenContado(e.target.checked)} />} label={`Margen ${contadoHeader}`} />
              <FormControlLabel control={<Checkbox checked={modificarMargenMayor} onChange={(e) => setModificarMargenMayor(e.target.checked)} />} label={`Margen ${mayorHeader}`} />
              <FormControlLabel control={<Checkbox checked={modificarMargenMenor} onChange={(e) => setModificarMargenMenor(e.target.checked)} />} label={`Margen ${menorHeader}`} />
              <FormControlLabel control={<Checkbox checked={noModificarPrecioVenta} disabled={!hasMarginMode} onChange={(e) => setNoModificarPrecioVenta(e.target.checked)} />} label="No modificar venta" />
              <FormControlLabel control={<Checkbox checked={redondearSinDecimales} onChange={(e) => setRedondearSinDecimales(e.target.checked)} />} label="Sin decimales" />
              <FormControlLabel control={<Checkbox checked={ajustarDecimal99} onChange={(e) => setAjustarDecimal99(e.target.checked)} />} label="Ajustar ,99" />
              <FormControlLabel control={<Checkbox checked={aplicarATodoLoFiltrado} onChange={(e) => setAplicarATodoLoFiltrado(e.target.checked)} />} label={`Aplicar a todo lo filtrado (${total})`} />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <Button variant="outlined" startIcon={previewing ? <CircularProgress color="inherit" size={18} /> : <CalculateIcon />} disabled={previewing} onClick={handlePreview}>
                {previewing ? "Calculando..." : "Vista previa"}
              </Button>
              <Button variant="contained" color="success" startIcon={<SaveIcon />} disabled={preview.length === 0 || saving} onClick={() => setConfirmOpen(true)}>
                Aplicar cambios
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <DataGridViewport height={520} minWidth={1260} sx={{ mb: 2 }}>
        <DataGrid
          rows={productRows}
          columns={productColumns}
          checkboxSelection
          rowCount={total}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={(ids: any) => setSelectedIds(ids.map((id: any) => Number(id)))}
          pageSizeOptions={[10, 20, 50, 100]}
          loading={loading}
          disableRowSelectionOnClick
        />
      </DataGridViewport>

      {preview.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Vista previa ({preview.length})</Typography>
          <DataGridViewport height={360} minWidth={1280}>
            <DataGrid rows={previewRows} columns={previewColumns} pageSizeOptions={[10, 20, 50]} disableRowSelectionOnClick />
          </DataGridViewport>
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar modificacion masiva</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Se modificaran {preview.length} productos. Esta operacion actualiza precios y guarda historial.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" color="success" startIcon={saving ? <CircularProgress color="inherit" size={18} /> : <SearchIcon />} onClick={handleApply} disabled={saving}>
            {saving ? "Aplicando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ModificacionMasivaPrecios;





